/**
 * Meta Pixel - Consent-gated implementation med full funnel-eventstruktur
 *
 * TOFU (Top of Funnel):
 *   - PageView: Automatiskt vid varje sidladdning
 *   - ViewContent: Vid besök på specifika produktsidor/paketsidor
 *
 * MOFU (Middle of Funnel):
 *   - Lead: Vid offertformulär-inskick (namn, email, telefon)
 *   - Schedule: Vid val av hembesökstid
 *   - AddToWishlist: Vid klick på "Spara paket" eller quiz-resultat
 *
 * BOFU (Bottom of Funnel):
 *   - CompleteRegistration: Vid bekräftad offertförfrågan (tack-sida)
 *   - Contact: Vid telefonklick eller direktkontakt
 *
 * Pixel ID: 1511763713910445
 */

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: (...args: unknown[]) => void;
  }
}

const PIXEL_ID = "1511763713910445";
let pixelLoaded = false;

/**
 * Injicera Meta Pixel-scriptet i DOM.
 * NOTE: Meta Pixel bör nu initieras via GTM-containern (GTM-MMS2MD4L).
 * Denna funktion behålls som fallback om pixeln inte laddas via GTM.
 * CMP-consent hanteras av användarens egen CMP via GTM.
 */
export function initMetaPixel(): void {
  if (pixelLoaded) return;
  if (typeof window === "undefined") return;
  pixelLoaded = true;

  // Meta Pixel base code
  const f = window;
  const b = document;
  if (typeof f.fbq === "function") return;

  const n: any = (f.fbq = function (...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod.apply(n, args);
    } else {
      n.queue.push(args);
    }
  });

  if (!f._fbq) f._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];

  const t = b.createElement("script");
  t.async = true;
  t.src = "https://connect.facebook.net/en_US/fbevents.js";
  const s = b.getElementsByTagName("script")[0];
  s?.parentNode?.insertBefore(t, s);

  // Init pixel
  window.fbq("init", PIXEL_ID);

  // Automatic PageView on init
  window.fbq("track", "PageView");
}

/**
 * Kontrollera om pixeln är laddad (för att undvika anrop före consent)
 */
function isPixelReady(): boolean {
  return pixelLoaded && typeof window !== "undefined" && typeof window.fbq === "function";
}

/* ═══════════════════════════════════════════════════════
   TOFU - Top of Funnel Events
   ═══════════════════════════════════════════════════════ */

/**
 * Tracka PageView vid SPA-navigering.
 * Anropas vid varje route-ändring.
 */
export function trackPageView(): void {
  if (!isPixelReady()) return;
  window.fbq("track", "PageView");
}

/**
 * Tracka ViewContent vid besök på produktsidor, paketsidor eller artiklar.
 * Ger Meta data för att bygga retargeting-audiences baserat på intresse.
 */
export function trackViewContent(params: {
  content_name: string;
  content_category: string;
  content_type?: string;
  value?: number;
  currency?: string;
}): void {
  if (!isPixelReady()) return;
  window.fbq("track", "ViewContent", {
    content_name: params.content_name,
    content_category: params.content_category,
    content_type: params.content_type || "product",
    value: params.value,
    currency: params.currency || "SEK",
  });
}

/* ═══════════════════════════════════════════════════════
   MOFU - Middle of Funnel Events
   ═══════════════════════════════════════════════════════ */

/**
 * Tracka Lead vid offertformulär-inskick.
 * Skickar Advanced Matching-data för bättre attribution.
 */
export function trackLead(params: {
  content_name?: string;
  value?: number;
  currency?: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}): void {
  if (!isPixelReady()) return;

  // Advanced Matching: uppdatera user data
  if (params.email || params.phone || params.firstName) {
    const userData: Record<string, string> = {};
    if (params.email) userData.em = params.email.toLowerCase().trim();
    if (params.phone) userData.ph = params.phone.replace(/[^0-9+]/g, "");
    if (params.firstName) userData.fn = params.firstName.toLowerCase().trim();
    if (params.lastName) userData.ln = params.lastName.toLowerCase().trim();
    window.fbq("init", PIXEL_ID, userData);
  }

  window.fbq("track", "Lead", {
    content_name: params.content_name || "Offertförfrågan",
    value: params.value || 0,
    currency: params.currency || "SEK",
  });
}

/**
 * Tracka Schedule vid val av hembesökstid.
 * Custom event för MOFU-retargeting.
 */
export function trackSchedule(params: {
  content_name?: string;
}): void {
  if (!isPixelReady()) return;
  window.fbq("track", "Schedule", {
    content_name: params.content_name || "Hembesök",
  });
}

/**
 * Tracka AddToWishlist vid quiz-resultat eller "spara paket".
 * Används för retargeting av engagerade besökare.
 */
export function trackAddToWishlist(params: {
  content_name: string;
  content_category?: string;
  value?: number;
  currency?: string;
}): void {
  if (!isPixelReady()) return;
  window.fbq("track", "AddToWishlist", {
    content_name: params.content_name,
    content_category: params.content_category || "paket",
    value: params.value,
    currency: params.currency || "SEK",
  });
}

/* ═══════════════════════════════════════════════════════
   BOFU - Bottom of Funnel Events
   ═══════════════════════════════════════════════════════ */

/**
 * Tracka CompleteRegistration vid bekräftad offertförfrågan.
 * Primärt konverteringsmål för Meta Ads-optimering.
 */
export function trackCompleteRegistration(params: {
  content_name?: string;
  value?: number;
  currency?: string;
  status?: string;
}): void {
  if (!isPixelReady()) return;
  window.fbq("track", "CompleteRegistration", {
    content_name: params.content_name || "Offert bekräftad",
    value: params.value || 0,
    currency: params.currency || "SEK",
    status: params.status || "complete",
  });
}

/**
 * Tracka Contact vid telefonklick eller direktkontakt.
 * Custom event för BOFU-retargeting.
 */
export function trackContact(params: {
  content_name?: string;
  method?: string;
}): void {
  if (!isPixelReady()) return;
  window.fbq("track", "Contact", {
    content_name: params.content_name || "Kontakt",
    method: params.method || "phone",
  });
}

/* ═══════════════════════════════════════════════════════
   Custom Events (för granulär retargeting)
   ═══════════════════════════════════════════════════════ */

/**
 * Tracka custom event för specifika interaktioner.
 * Användbart för att bygga custom audiences.
 */
export function trackCustomEvent(eventName: string, params?: Record<string, unknown>): void {
  if (!isPixelReady()) return;
  window.fbq("trackCustom", eventName, params);
}
